import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, setToken, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import Spade from "@/components/Spade";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
    const navigate = useNavigate();
    const { login, status, refresh } = useAuth();
    const [mode, setMode] = useState("checking"); // 'checking' | 'setup' | 'login'
    const [adminEmail, setAdminEmail] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (status === "authed") navigate("/admin/dashboard", { replace: true });
    }, [status, navigate]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auth/setup-status");
                if (data.admin_email) {
                    setAdminEmail(data.admin_email);
                    setEmail(data.admin_email);
                }
                setMode(data.needs_setup ? "setup" : "login");
            } catch {
                setMode("login");
            }
        })();
    }, []);

    const submitLogin = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) navigate("/admin/dashboard", { replace: true });
        else setError(res.error || "Login failed");
    };

    const submitSetup = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirmPw) {
            setError("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/setup", {
                email: adminEmail || email,
                password,
                name: "Aestherium Admin",
            });
            setToken(data.token);
            await refresh();
            toast.success("Admin account created. Signed in.");
            navigate("/admin/dashboard", { replace: true });
        } catch (err) {
            setError(formatApiError(err, "Setup failed"));
        } finally {
            setLoading(false);
        }
    };

    const isSetup = mode === "setup";

    return (
        <div
            data-testid={ADMIN.loginRoot}
            className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
        >
            <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#4A0505] via-[#3B0404] to-[#0A0000]" />
            <div className="absolute right-[-6%] top-[-4%] opacity-[0.05]">
                <Spade size={520} />
            </div>

            {mode === "checking" ? (
                <p className="text-[var(--accent-champagne)] soft-pulse font-display text-xl">
                    Loading…
                </p>
            ) : (
                <form
                    onSubmit={isSetup ? submitSetup : submitLogin}
                    className="w-full max-w-md surface-glass rounded-3xl border border-[var(--border-subtle)] p-8 md:p-10 relative"
                    data-testid={isSetup ? "admin-setup-form" : "admin-login-form"}
                >
                    <div className="flex items-center gap-2 mb-8">
                        <Spade size={26} />
                        <span className="font-display text-2xl text-[var(--text-primary)]">
                            Aestherium<span className="text-[var(--accent-gold)]">.</span>
                        </span>
                    </div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent-gold)] mb-3 inline-flex items-center gap-2">
                        {isSetup && <ShieldCheck size={13} />}
                        {isSetup ? "First-time Setup" : "Admin Console"}
                    </p>
                    <h1 className="font-display text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-3 leading-tight">
                        {isSetup ? "Set your password." : "Sign in."}
                    </h1>
                    {isSetup && (
                        <p className="text-[var(--text-secondary)] text-sm mb-8 leading-relaxed">
                            No password has been set for{" "}
                            <span className="text-[var(--accent-champagne)]">
                                {adminEmail}
                            </span>{" "}
                            yet. Choose one now — this form disables itself the moment your account is created.
                        </p>
                    )}
                    {!isSetup && <div className="mb-8" />}

                    <label className="block text-xs uppercase tracking-[0.25em] text-[var(--accent-champagne)]/70 mb-2">
                        Email
                    </label>
                    <input
                        data-testid={ADMIN.loginEmail}
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isSetup}
                        className="w-full mb-5 bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)] disabled:opacity-60"
                    />

                    <label className="block text-xs uppercase tracking-[0.25em] text-[var(--accent-champagne)]/70 mb-2">
                        {isSetup ? "New Password" : "Password"}
                    </label>
                    <input
                        data-testid={ADMIN.loginPassword}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={isSetup ? 8 : 1}
                        className="w-full mb-5 bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]"
                    />

                    {isSetup && (
                        <>
                            <label className="block text-xs uppercase tracking-[0.25em] text-[var(--accent-champagne)]/70 mb-2">
                                Confirm Password
                            </label>
                            <input
                                data-testid="admin-setup-confirm"
                                type="password"
                                value={confirmPw}
                                onChange={(e) => setConfirmPw(e.target.value)}
                                required
                                minLength={8}
                                className="w-full mb-6 bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]"
                            />
                        </>
                    )}

                    {!isSetup && <div className="mb-1" />}

                    {error && (
                        <p
                            data-testid={ADMIN.loginError}
                            className="mb-5 text-sm text-red-300 bg-red-900/30 border border-red-500/30 rounded-lg px-4 py-3"
                        >
                            {error}
                        </p>
                    )}

                    <button
                        data-testid={isSetup ? "admin-setup-submit" : ADMIN.loginSubmit}
                        type="submit"
                        disabled={loading}
                        className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm hover:bg-[var(--accent-champagne)] transition-colors disabled:opacity-70"
                    >
                        {loading
                            ? isSetup
                                ? "Creating…"
                                : "Signing in…"
                            : isSetup
                              ? "Create admin"
                              : "Sign in"}
                        <ArrowRight size={16} />
                    </button>

                    <a
                        href="/"
                        className="mt-6 text-xs text-[var(--text-secondary)] hover:text-[var(--accent-champagne)] block text-center"
                    >
                        ← back to site
                    </a>
                </form>
            )}
        </div>
    );
}
